import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useNavigationBlocker } from '@rad/hooks/useRouting';
import Modal from '@rad/components/ui/modal';
// Object keys in a fixed order, so two copies of a form compare by what they hold.
const sortKeys = (_key, value) => typeof value === 'object' && value !== null && !Array.isArray(value)
    ? Object.fromEntries(Object.entries(value).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0))
    : value;
// Whether two copies of a form hold the same thing: what a save would send, whatever order the keys are in.
export const isSameForm = (a, b) => JSON.stringify(a, sortKeys) === JSON.stringify(b, sortKeys);
// Asks before edits that haven't been saved are lost. While `isDirty`, leaving in the app opens the
// dialog in `element`, closing or reloading the tab gets the browser's own warning, and `confirmLeave`
// asks the same before anything else that would drop them.
export const useUnsavedChanges = (isDirty) => {
    const blocker = useNavigationBlocker(isDirty);
    const [pending, setPending] = useState(null);
    const isOpen = blocker.blocked || pending !== null;
    // Set once the person chooses to leave, so a move that unloads the page doesn't ask a second time.
    const leaving = useRef(false);
    const keepEditingRef = useRef(null);
    // Keep editing takes focus once the question shows, so a stray Enter keeps the edits. The modal is
    // still hidden when this commits, so the focus waits a tick for it to show.
    useEffect(() => {
        if (!isOpen)
            return;
        const timer = setTimeout(() => { var _a; return (_a = keepEditingRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 0);
        return () => clearTimeout(timer);
    }, [isOpen]);
    useEffect(() => {
        if (!isDirty)
            return;
        leaving.current = false;
        const warn = (event) => {
            if (leaving.current)
                return;
            event.preventDefault();
            // Older browsers ask only when this is set.
            event.returnValue = true;
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [isDirty]);
    const confirmLeave = (go) => {
        if (isDirty)
            setPending(() => go);
        else
            go();
    };
    const stay = () => {
        setPending(null);
        blocker.cancel();
    };
    const leave = () => {
        leaving.current = true;
        setPending(null);
        if (pending)
            pending();
        else
            blocker.proceed();
    };
    return {
        confirmLeave,
        element: (_jsx(Modal, { isOpen: isOpen, title: "Leave without saving?", onClose: stay, footer: _jsxs(_Fragment, { children: [_jsx("button", { type: "button", className: "btn btn-outline-danger", onClick: leave, children: "Leave without saving" }), _jsx("button", { ref: keepEditingRef, type: "button", className: "btn btn-primary", onClick: stay, children: "Keep editing" })] }), children: _jsx("p", { className: "mb-0", children: "You have changes that haven't been saved. If you leave now, they'll be lost." }) })),
    };
};
